import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { useClients, useContracts, useConvenios, useTarifas, useComisionConfig } from '../contexts/DataContext';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Input } from '../components/input';
import { Button } from '../components/button';
import SearchableListbox from '../components/SearchableListbox';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../components/table';
import { FianzaView } from '../components/FianzaView';
import { ContratoView } from '../components/ContratoView';
import AssignedUsersBubbles from '../components/AssignedUsersBubbles';
import UserAssignmentSelector from '../components/UserAssignmentSelector';
import { Dialog, DialogTitle, DialogBody } from '../components/dialog';
import { useData } from '../contexts/DataContext';
import {
  calculateTarifa,
  getEmisorNombre,
  getSubRamo,
  getTipoFianza,
  getFacturasVigentes,
  toTimestamp,
} from '../utils/calculations';
import {
  computePendingFianzas,
  dueLabel,
  getFianzaStatus,
} from '../utils/convenioCompliance';
import { useFrozenColumns, frozenCellClass, frozenHeadClass } from '../hooks/useFrozenColumns';
import {
  ensureFacturas,
  getEventoTipos,
  ENDOSO_TIPOS,
  getEndososAbiertos,
  clasificarEndosoPendiente,
  ESTADO_FIANZA,
} from '../utils/fianzaEventos';
import { useComisionLedger } from '../hooks/useComisionLedger';
import { indexMovementsByFianza, resolveComision } from '../utils/comisionReconcile';
import { PencilSquareIcon, ArrowUpIcon, ArrowDownIcon, CameraIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useUser } from '../components/UserContext';
import { logBitacora, computeUserAssignmentChanges } from '../utils/bitacora';

const EXPORT_ENABLED = process.env.REACT_APP_ENABLE_EXPORT === 'true';

const ENDOSO_SET = new Set(ENDOSO_TIPOS);
const URGENCIA_ORDER = { vencido: 0, urgente: 1, proximo: 2, normal: 3, sinFecha: 4 };
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const STATUS_BADGE = {
  vigente: 'bg-green-100 text-green-800',
  vencida: 'bg-red-100 text-red-800',
  pendiente: 'bg-gray-100 text-gray-800',
  cancelada: 'bg-zinc-200 text-zinc-700',
  anulada: 'bg-zinc-200 text-zinc-700',
  sustituida: 'bg-zinc-200 text-zinc-700',
};

const PAGO_BADGE = {
  pagada: 'bg-green-100 text-green-800',
  parcial: 'bg-amber-100 text-amber-800',
  pendiente: 'bg-orange-100 text-orange-800',
};

const PAGO_ORDER = { pendiente: 0, parcial: 1, pagada: 2 };

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

const pagoLabel = (row) =>
  row.pagoEstado === 'pagada'
    ? 'Pagada'
    : row.pagoEstado === 'parcial'
    ? `Parcial ${row.pagoPagadas}/${row.pagoTotal}`
    : 'Pendiente';

// Selecting this tile swaps the table for the requisitos-por-emitir view: a
// requisito is not a fianza, so it cannot be a predicate over `baseFianzas`
// like every other tile.
const POR_EMITIR = 'porEmitir';

const TILES = [
  { key: 'vigentes', label: 'Vigentes', color: 'text-green-600' },
  { key: 'porVencer', label: 'Por vencer (30 días)', color: 'text-amber-600' },
  { key: 'vencidas', label: 'Vencidas', color: 'text-red-600' },
  { key: 'endosos', label: 'Endoso pendiente', color: 'text-red-600' },
  { key: 'sinPagar', label: 'Sin pagar', color: 'text-orange-600' },
  { key: 'terminadas', label: 'Terminadas', color: 'text-zinc-500' },
  { key: POR_EMITIR, label: 'Por emitir', color: 'text-amber-600' },
];

const TILE_PREDICATES = {
  vigentes: (f) => f.estatusCalculado === 'vigente',
  porVencer: (f) => f.porVencer30,
  vencidas: (f) => f.estatusCalculado === 'vencida',
  endosos: (f) => f.endososPendientesCount > 0,
  sinPagar: (f) => f.status.estado === ESTADO_FIANZA.ACTIVA && f.pagoEstado !== 'pagada',
  terminadas: (f) => f.status.estado !== ESTADO_FIANZA.ACTIVA,
};

const Fianzas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const syncRef = useRef(false);
  const paramsHydratedRef = useRef(false);
  const hydrationTargetRef = useRef(null);
  const { clientsList, clientsById, clientsLoading } = useClients();
  const { contractsList, contractsLoading } = useContracts();
  const { updateContractAssignedUsers } = useData();
  const { conveniosById } = useConvenios();
  const { tarifas } = useTarifas();
  const { config: comisionConfig } = useComisionConfig();
  const { movements } = useComisionLedger();
  const { user, userData, getUserName, isOwner } = useUser();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedEstatus, setSelectedEstatus] = useState('all');
  const [selectedPago, setSelectedPago] = useState('all');
  const [selectedTile, setSelectedTile] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [isContratoViewOpen, setIsContratoViewOpen] = useState(false);
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);
  const [selectedFianzaForAssignment, setSelectedFianzaForAssignment] = useState(null);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [estatusTooltip, setEstatusTooltip] = useState({ visible: false, x: 0, y: 0 });
  const estatusRef = useRef(null);
  const exportRef = useRef(null);
  const isPorEmitir = selectedTile === POR_EMITIR;
  // Freeze panes, keyed on the view: the two tables swap places, so the
  // measured widths have to be taken again when they do.
  // Emitidas: Cliente and Número de Fianza stay put while the rest scrolls.
  const frozen = useFrozenColumns(2, isPorEmitir);
  // Por emitir: Cliente and Tipo.
  const frozenPend = useFrozenColumns(2, isPorEmitir);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFrom, setExportFrom] = useState(1);
  const [exportTo, setExportTo] = useState(10);

  const byFianza = useMemo(() => indexMovementsByFianza(movements), [movements]);

  const handleExportImage = useCallback(async () => {
    setIsExporting(true);
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    await document.fonts.ready;
    await new Promise(r => setTimeout(r, 100));
    try {
      const dataUrl = await toPng(exportRef.current, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });
      const link = document.createElement('a');
      link.download = `fianzas-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  }, []);

  // Sort contracts by ID for stable ordering
  const contratos = useMemo(() =>
    [...contractsList].sort((a, b) => (a.id || '').localeCompare(b.id || '')),
    [contractsList]
  );
  const clientsData = useMemo(() => clientsById, [clientsById]);
  const clientNames = useMemo(() => {
    const names = {};
    clientsList.forEach((cliente) => {
      names[cliente.id] = cliente.nombreEmpresa || "Sin nombre";
    });
    return names;
  }, [clientsList]);
  const loading = clientsLoading || contractsLoading;

  // Debug logger
  useEffect(() => {
    console.log('[Fianzas] location.search', location.search);
  }, [location.search]);
  useEffect(() => {
    console.log('[Fianzas] paramsHydratedRef', paramsHydratedRef.current);
  });
  useEffect(() => {
    console.log('[Fianzas] currentPage/itemsPerPage', currentPage, itemsPerPage);
  }, [currentPage, itemsPerPage]);

  // Reset to first page when filters change (skip initial render and wait for params hydration)
  const resetGuardRef = useRef(true);
  useEffect(() => {
    if (resetGuardRef.current) {
      resetGuardRef.current = false;
      return;
    }
    if (!paramsHydratedRef.current) return;
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage, selectedClient, selectedEstatus, selectedPago, selectedTile, sortBy, sortOrder]);

  // Seed state from URL params (page/perPage/estatus/pago) and keep in sync without loops
  useEffect(() => {
    if (syncRef.current) {
      syncRef.current = false;
      return;
    }
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page') || '1', 10);
    const perPageParam = parseInt(params.get('perPage') || `${itemsPerPage}`, 10);
    const estatusParam = params.get('estatus');
    const pagoParam = params.get('pago');

    console.log('[Fianzas] seeding from params', { pageParam, perPageParam });
    hydrationTargetRef.current = {
      page: !Number.isNaN(pageParam) && pageParam > 0 ? pageParam : 1,
      perPage: !Number.isNaN(perPageParam) && perPageParam > 0 ? perPageParam : itemsPerPage,
      done: false,
    };
    if (!Number.isNaN(pageParam) && pageParam > 0) {
      setCurrentPage(pageParam);
    }
    if (!Number.isNaN(perPageParam) && perPageParam > 0) {
      setItemsPerPage(perPageParam);
    }
    if (['vigente', 'pendiente', 'vencida', 'cancelada', 'anulada', 'sustituida'].includes(estatusParam)) {
      setSelectedEstatus(estatusParam);
    }
    if (['pagada', 'parcial', 'pendiente'].includes(pagoParam)) {
      setSelectedPago(pagoParam);
    }
    paramsHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Mark hydration done when state matches seeded values
  useEffect(() => {
    const target = hydrationTargetRef.current;
    if (!target || target.done === true) return;
    if (currentPage === target.page && itemsPerPage === target.perPage) {
      hydrationTargetRef.current = { ...target, done: true };
    }
  }, [currentPage, itemsPerPage]);

  // Keep URL in sync with current page/perPage/filters for shareable deep-links
  useEffect(() => {
    if (!paramsHydratedRef.current) return;
    const target = hydrationTargetRef.current;
    if (target && target.done === false) {
      return; // wait until hydration settles
    }
    console.log('[Fianzas] syncing to URL', { currentPage, itemsPerPage });
    const params = new URLSearchParams(location.search);
    params.set('page', currentPage);
    params.set('perPage', itemsPerPage);
    if (selectedEstatus !== 'all') params.set('estatus', selectedEstatus);
    else params.delete('estatus');
    if (selectedPago !== 'all') params.set('pago', selectedPago);
    else params.delete('pago');
    const newSearch = params.toString();
    const currentSearch = new URLSearchParams(location.search).toString();
    if (newSearch !== currentSearch) {
      syncRef.current = true;
      navigate({ pathname: '/app/fianzas', search: newSearch }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, selectedEstatus, selectedPago, location.search]);

  // A convenio contract has no cliente of its own — it is labelled by the
  // convenio and its beneficiario. Both the emitidas and the por-emitir views
  // name rows the same way, so the rule lives in one place.
  const resolveClientName = useCallback((contrato) => {
    if (!contrato) return 'Sin nombre';
    if (contrato.convenioId) {
      const conv = conveniosById[contrato.convenioId];
      const ben = (conv?.beneficiarios || []).find(b => b.id === contrato.beneficiarioId);
      return `${conv?.nombre || 'Convenio'} ${ben ? `- ${ben.nombre}` : ''}`;
    }
    return clientNames[contrato.clientId] || 'Sin nombre';
  }, [conveniosById, clientNames]);

  // Every fianza in the org, enriched once: lifecycle status, per-factura pago,
  // endosos, and ledger-reconciled comisión (same resolver the reportes use).
  const allFianzas = useMemo(() => {
    const now = Date.now();
    const out = [];
    contratos.forEach(contrato => {
      if (!contrato.fianzas || contrato.fianzas.length === 0) return;
      const splitOpts = {
        contractData: contrato,
        orgConfig: comisionConfig,
        convenioData: contrato.convenioId ? conveniosById?.[contrato.convenioId] : null,
      };
      const clientName = resolveClientName(contrato);

      contrato.fianzas.forEach(fianza => {
        const status = getFianzaStatus(fianza, now);
        const montoAfianzado = Number(fianza.montoAfianzado) || 0;
        const vigencia = {
          inicio: toTimestamp(fianza.inicioVigenciaFianza),
          fin: toTimestamp(fianza.finVigenciaFianza),
        };

        const { prima, comisionMonto, comisionPct, pctEstimado } = resolveComision(
          fianza,
          byFianza.get(fianza.id),
          tarifas,
          getEmisorNombre(fianza),
          getSubRamo(fianza),
          getTipoFianza(fianza),
          splitOpts
        );
        const tarifaCalculada = calculateTarifa(prima, montoAfianzado, { vigencia });

        const facturasVigentes = getFacturasVigentes(ensureFacturas(fianza));
        const pagoTotal = facturasVigentes.length;
        const pagoPagadas = facturasVigentes.filter(f => f.pagada).length;
        const pagoEstado =
          pagoTotal > 0 && pagoPagadas === pagoTotal ? 'pagada' : pagoPagadas > 0 ? 'parcial' : 'pendiente';

        const endososCount = (fianza.eventos || []).filter(e =>
          getEventoTipos(e).some(t => ENDOSO_SET.has(t))
        ).length;
        const abiertos = status.estado === ESTADO_FIANZA.ACTIVA ? getEndososAbiertos(fianza) : [];
        let endososUrgencia = null;
        abiertos.forEach(p => {
          const u = clasificarEndosoPendiente(p, now).code;
          if (!endososUrgencia || URGENCIA_ORDER[u] < URGENCIA_ORDER[endososUrgencia]) endososUrgencia = u;
        });

        const fin = fianza.finVigenciaFianza?.toMillis?.() || fianza.finVigenciaFianza || 0;
        const porVencer30 = status.code === 'vigente' && fin > 0 && fin - now <= THIRTY_DAYS;

        out.push({
          ...fianza,
          contratoId: contrato.id,
          contratoTitle: contrato.tituloContrato?.value || 'Sin título',
          contratoNumero: contrato.numeroContrato?.value || '',
          clientId: contrato.clientId,
          convenioId: contrato.convenioId || null,
          clientName,
          numeroPoliza: fianza.numeroFianza || 'N/A',
          tipoFianza: fianza.tipo || fianza.tipoFianza || 'N/A',
          contractAssignedUsers: contrato.assignedUsers || [],
          status,
          estatusCalculado: status.code,
          tarifaCalculada,
          primaResuelta: prima,
          comisionMonto,
          comisionPct,
          pctEstimado,
          pagoEstado,
          pagoPagadas,
          pagoTotal,
          endososCount,
          endososPendientesCount: abiertos.length,
          endososUrgencia,
          porVencer30,
        });
      });
    });
    return out;
  }, [contratos, conveniosById, resolveClientName, tarifas, byFianza, comisionConfig]);

  // Requisitos de fianza sin emitir: `fianzasRequeridas` entries on a contrato
  // that no fianza has been linked to yet. These are NOT in `allFianzas` —
  // nothing has been emitted — so they carry the contrato's identity instead of
  // a fianza's, and the money columns have no counterpart.
  const allPendientes = useMemo(() => {
    const byId = new Map(contratos.map(c => [c.id, c]));
    return computePendingFianzas(contratos).map((fr) => {
      const contrato = byId.get(fr.contractId);
      return {
        ...fr,
        clientId: contrato?.clientId || null,
        convenioId: contrato?.convenioId || null,
        clientName: resolveClientName(contrato),
        contratoTitle: contrato?.tituloContrato?.value || 'Sin título',
        contratoNumero: contrato?.numeroContrato?.value || '',
        tipoFianza: fr.tipo || 'Fianza',
      };
    });
  }, [contratos, resolveClientName]);

  // Cliente + search narrow the universe; the tiles summarize THAT universe,
  // and estatus/pago/tile then narrow the table below them.
  const baseFianzas = useMemo(() => {
    let fianzas = allFianzas;
    if (selectedClient !== 'all') {
      fianzas = fianzas.filter(f => f.clientId === selectedClient);
    }
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      fianzas = fianzas.filter(f =>
        f.clientName.toLowerCase().includes(searchLower) ||
        f.contratoTitle.toLowerCase().includes(searchLower) ||
        (f.contratoNumero?.toLowerCase() || '').includes(searchLower) ||
        (f.tipoFianza?.toLowerCase() || '').includes(searchLower) ||
        (f.numeroPoliza?.toLowerCase() || '').includes(searchLower)
      );
    }
    return fianzas;
  }, [allFianzas, selectedClient, searchTerm]);

  const basePendientes = useMemo(() => {
    let pendientes = allPendientes;
    if (selectedClient !== 'all') {
      pendientes = pendientes.filter(p => p.clientId === selectedClient);
    }
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      pendientes = pendientes.filter(p =>
        p.clientName.toLowerCase().includes(searchLower) ||
        p.contratoTitle.toLowerCase().includes(searchLower) ||
        (p.tipoFianza?.toLowerCase() || '').includes(searchLower) ||
        (p.contractNumero?.toLowerCase() || '').includes(searchLower)
      );
    }
    return pendientes;
  }, [allPendientes, selectedClient, searchTerm]);

  const tileCounts = useMemo(() => {
    const counts = {};
    TILES.forEach(({ key }) => {
      counts[key] = key === POR_EMITIR
        ? basePendientes.length
        : baseFianzas.filter(TILE_PREDICATES[key]).length;
    });
    return counts;
  }, [baseFianzas, basePendientes]);

  const fianzasData = useMemo(() => {
    let fianzas = baseFianzas;

    if (selectedEstatus !== 'all') {
      fianzas = fianzas.filter(f => f.estatusCalculado === selectedEstatus);
    }
    if (selectedPago !== 'all') {
      fianzas = fianzas.filter(f => f.pagoEstado === selectedPago);
    }
    if (selectedTile && TILE_PREDICATES[selectedTile]) {
      fianzas = fianzas.filter(TILE_PREDICATES[selectedTile]);
    }

    return [...fianzas].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'createdAt':
          comparison = (a.createdAt || 0) - (b.createdAt || 0);
          break;
        case 'inicioVigencia':
          comparison = (a.inicioVigenciaFianza || 0) - (b.inicioVigenciaFianza || 0);
          break;
        case 'finVigencia':
          comparison = (a.finVigenciaFianza || 0) - (b.finVigenciaFianza || 0);
          break;
        case 'monto':
          comparison = (a.monto || 0) - (b.monto || 0);
          break;
        case 'cliente':
          comparison = (a.clientName || '').localeCompare(b.clientName || '');
          break;
        case 'numeroPoliza':
          comparison = (a.numeroPoliza || '').localeCompare(b.numeroPoliza || '');
          break;
        case 'estatus':
          comparison = (a.estatusCalculado || '').localeCompare(b.estatusCalculado || '');
          break;
        case 'pago':
          comparison = PAGO_ORDER[a.pagoEstado] - PAGO_ORDER[b.pagoEstado];
          break;
        default:
          comparison = (a.createdAt || 0) - (b.createdAt || 0);
      }

      // Apply sort order
      comparison = sortOrder === 'asc' ? comparison : -comparison;

      // Secondary sort by ID for stable ordering when primary is equal
      if (comparison === 0) {
        const idA = `${a.contratoId}-${a.id || ''}`;
        const idB = `${b.contratoId}-${b.id || ''}`;
        return idA.localeCompare(idB);
      }

      return comparison;
    });
  }, [baseFianzas, selectedEstatus, selectedPago, selectedTile, sortBy, sortOrder]);

  // Requisitos have no estatus, no pago and no vigencia of their own, so the
  // fianza sorts don't carry over. Default order is by urgency — atrasadas
  // first, then by due date, with the futuras (vicios ocultos y demás
  // requisitos posteriores al término) last, since those cannot be emitted yet.
  const pendientesData = useMemo(() => {
    const byUrgency = (a, b) => {
      if (a.isFutura !== b.isFutura) return a.isFutura ? 1 : -1;
      const da = a.dueDate == null ? Infinity : a.dueDate;
      const db = b.dueDate == null ? Infinity : b.dueDate;
      if (da !== db) return da - db;
      return a._key.localeCompare(b._key);
    };
    const sorted = [...basePendientes];
    if (sortBy === 'cliente') {
      sorted.sort((a, b) => (a.clientName || '').localeCompare(b.clientName || ''));
    } else if (sortBy === 'monto') {
      sorted.sort((a, b) => (Number(a.monto) || 0) - (Number(b.monto) || 0));
    } else {
      sorted.sort(byUrgency);
      return sorted;
    }
    return sortOrder === 'asc' ? sorted : sorted.reverse();
  }, [basePendientes, sortBy, sortOrder]);

  // Whichever view is on screen drives pagination.
  const rowsTotal = isPorEmitir ? pendientesData.length : fianzasData.length;

  useEffect(() => {
    console.log('[Fianzas] fianzasData length', fianzasData.length);
  }, [fianzasData.length]);

  // Clamp page if data shrinks (skip while loading or empty; also skip while hydrating)
  useEffect(() => {
    if (loading) return;
    const target = hydrationTargetRef.current;
    if (target && target.done === false) return;
    console.log('[Fianzas] clamp check', { length: rowsTotal, currentPage, itemsPerPage });
    if (rowsTotal === 0) return;
    const maxPage = Math.max(1, Math.ceil(rowsTotal / itemsPerPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [loading, rowsTotal, itemsPerPage, currentPage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  const totalPages = Math.ceil(rowsTotal / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFianzas = fianzasData.slice(startIndex, endIndex);
  const paginatedPendientes = pendientesData.slice(startIndex, endIndex);

  const handleFianzaClick = (fianza) => {
    setSelectedFianza(fianza);
    setIsFianzaViewOpen(true);
  };
  // A requisito has no fianza to open, so it opens the contrato that declares
  // it — where the fianza actually gets uploaded.
  const handleRequisitoClick = (req) => {
    const contrato = contratos.find(c => c.id === req.contractId);
    if (!contrato) return;
    setSelectedContrato(contrato);
    setIsContratoViewOpen(true);
  };
  const handleFianzaViewClose = () => {
    setIsFianzaViewOpen(false);
    setSelectedFianza(null);
  };

  // Open the sucesora/antecesora of a sustitución or renovación (same contrato).
  const openRelatedFianza = (row, targetFianzaId) => {
    if (!targetFianzaId) return;
    const enriched = allFianzas.find(f => f.contratoId === row.contratoId && f.id === targetFianzaId);
    if (!enriched) return;
    setSelectedFianza(enriched);
    setIsFianzaViewOpen(true);
  };

  const relacionChip = (fianza) => {
    if (fianza.sustituidaPor) {
      return { label: 'Ver sustituta', targetId: fianza.sustituidaPor };
    }
    if (fianza.renovadaPor) {
      return { label: 'Ver renovación', targetId: fianza.renovadaPor };
    }
    if (fianza.sustituyeA) {
      return { label: 'Sustituye anterior', targetId: fianza.sustituyeA };
    }
    if (fianza.renuevaA) {
      return { label: 'Renueva anterior', targetId: fianza.renuevaA };
    }
    return null;
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-4">
        <Heading level={1} className="text-azul-surelia mb-1">Fianzas</Heading>
        <Text className="text-zinc-600">Visualiza y gestiona todas las fianzas</Text>
      </div>

      {/* Resumen (los tiles filtran la tabla) */}
      <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {TILES.map(({ key, label, color }) => {
          const active = selectedTile === key;
          return (
            <button
              key={key}
              onClick={() => setSelectedTile(active ? null : key)}
              className={`text-left bg-white rounded-lg border p-3 shadow-sm transition-all duration-200 hover:shadow-md ${
                active ? 'border-azul-surelia ring-2 ring-azul-surelia/30' : 'border-zinc-200 hover:border-azul-surelia/40'
              }`}
            >
              <div className={`text-2xl font-bold tabular-nums ${tileCounts[key] > 0 ? color : 'text-zinc-300'}`}>
                {tileCounts[key]}
              </div>
              <Text className="text-xs text-zinc-600">{label}</Text>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white rounded-lg shadow-sm border border-zinc-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Cliente:</label>
            <SearchableListbox
              value={selectedClient}
              onChange={setSelectedClient}
              options={[
                { value: 'all', label: 'Todos los clientes' },
                ...Object.entries(clientNames)
                  .sort(([, a], [, b]) => a.localeCompare(b))
                  .map(([id, name]) => ({ value: id, label: name })),
              ]}
              className="min-w-[240px] max-w-[260px]"
              placeholder="Selecciona cliente"
              searchPlaceholder="Buscar cliente..."
              popupClassName="w-[260px]"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Estatus:</label>
            <select
              value={selectedEstatus}
              onChange={(e) => setSelectedEstatus(e.target.value)}
              disabled={isPorEmitir}
              title={isPorEmitir ? 'Un requisito por emitir todavía no tiene estatus' : undefined}
              className="px-3 py-1.5 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="all">Todos</option>
              <option value="vigente">Vigente</option>
              <option value="pendiente">Pendiente</option>
              <option value="vencida">Vencida</option>
              <option value="cancelada">Cancelada</option>
              <option value="anulada">Anulada</option>
              <option value="sustituida">Sustituida</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Pago:</label>
            <select
              value={selectedPago}
              onChange={(e) => setSelectedPago(e.target.value)}
              disabled={isPorEmitir}
              title={isPorEmitir ? 'Un requisito por emitir todavía no tiene prima que pagar' : undefined}
              className="px-3 py-1.5 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="all">Todos</option>
              <option value="pagada">Pagada</option>
              <option value="parcial">Pago parcial</option>
              <option value="pendiente">Pendiente</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Mostrar:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="px-3 py-1.5 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm min-w-[60px]"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>

          {/* Sort controls */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Ordenar:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm min-w-[140px]"
            >
              <option value="createdAt">Fecha de creación</option>
              <option value="inicioVigencia">Inicio vigencia</option>
              <option value="finVigencia">Fin vigencia</option>
              <option value="monto">Prima total</option>
              <option value="cliente">Cliente</option>
              <option value="numeroPoliza">No. Póliza</option>
              <option value="estatus">Estatus</option>
              <option value="pago">Pago</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 rounded-lg border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
              title={sortOrder === 'asc' ? 'Ascendente' : 'Descendente'}
            >
              {sortOrder === 'asc' ? (
                <ArrowUpIcon className="w-4 h-4" />
              ) : (
                <ArrowDownIcon className="w-4 h-4" />
              )}
            </button>
          </div>

          <Input
            type="text"
            placeholder="Buscar por cliente, contrato..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 sm:max-w-xs"
          />

          {EXPORT_ENABLED && !isPorEmitir && <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Rango:</label>
            <input
              type="number"
              min={1}
              max={fianzasData.length}
              value={exportFrom}
              onChange={(e) => setExportFrom(Math.max(1, Number(e.target.value)))}
              className="w-16 px-2 py-1.5 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm text-center"
            />
            <span className="text-sm text-zinc-500">a</span>
            <input
              type="number"
              min={1}
              max={fianzasData.length}
              value={exportTo}
              onChange={(e) => setExportTo(Math.max(1, Number(e.target.value)))}
              className="w-16 px-2 py-1.5 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm text-center"
            />
            <Button
              onClick={handleExportImage}
              disabled={isExporting}
              outline
              color="surelia"
              className="text-sm px-3 py-2 flex items-center gap-1.5"
              title="Exportar tabla como imagen"
            >
              <CameraIcon className="w-4 h-4" />
              {isExporting ? 'Exportando...' : 'Exportar'}
            </Button>
          </div>}
        </div>
      </div>

      {/* Fianzas table — or the requisitos por emitir, when that tile is on */}
      <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
        {isPorEmitir ? (
        <div className="relative">
          <Table onScroll={frozenPend.onScroll}>
            <TableHead className="bg-azul-surelia">
              <TableRow>
                <TableHeader
                  ref={frozenPend.setRef(0)}
                  style={{ left: frozenPend.offsets[0] }}
                  className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                >Cliente</TableHeader>
                <TableHeader
                  ref={frozenPend.setRef(1)}
                  style={{ left: frozenPend.offsets[1] }}
                  className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                >Tipo</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Estado</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">No. Contrato</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Título del Contrato</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">% del contrato</TableHeader>
                <TableHeader
                  className="text-white font-semibold py-3 border-b-0 text-right"
                  title="Monto que deberá quedar afianzado cuando se emita la fianza."
                >Por afianzar</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Emisión</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPendientes.map((req, idx) => {
                // Futura: no se puede emitir todavía (momento 'despues_termino',
                // p. ej. vicios ocultos), así que no debe leerse como urgente.
                const tone = req.isFutura
                  ? { badge: 'bg-zinc-100 text-zinc-600', label: 'Futura', due: 'text-zinc-500' }
                  : req.isOverdue
                  ? { badge: 'bg-red-100 text-red-800', label: 'Atrasada', due: 'text-red-700 font-medium' }
                  : { badge: 'bg-amber-100 text-amber-800', label: 'Por emitir', due: 'text-amber-700' };
                return (
                  <TableRow
                    key={req._key}
                    className={`group cursor-pointer transition-all duration-200 hover:bg-azul-surelia/5 ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                    }`}
                    onClick={() => handleRequisitoClick(req)}
                  >
                    <TableCell
                      style={{ left: frozenPend.offsets[0] }}
                      className={`py-3 ${frozenCellClass(idx)}`}
                    >
                      <Text className="text-sm font-medium text-azul-surelia">{req.clientName}</Text>
                    </TableCell>
                    <TableCell
                      style={{ left: frozenPend.offsets[1] }}
                      className={`py-3 ${frozenCellClass(idx)}`}
                    >
                      <Text className="text-sm font-semibold text-zinc-900">{req.tipoFianza}</Text>
                    </TableCell>
                    <TableCell className="py-3">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${tone.badge}`}>
                        {tone.label}
                      </span>
                    </TableCell>
                    <TableCell className="py-3">
                      <Text className="text-sm font-mono text-zinc-700">{req.contractNumero || '—'}</Text>
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="max-w-xs">
                        <Text className="text-sm text-zinc-900 truncate" title={req.contratoTitle}>
                          {req.contratoTitle}
                        </Text>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <Text className="text-sm text-zinc-900 tabular-nums">
                        {Number(req.porcentaje) > 0 ? `${Number(req.porcentaje)}%` : '—'}
                      </Text>
                    </TableCell>
                    <TableCell className="py-3 text-right">
                      <Text className="text-sm font-semibold text-azul-surelia tabular-nums">
                        {fmtMXN(Number(req.monto) || 0)}
                      </Text>
                    </TableCell>
                    <TableCell className="py-3">
                      <Text className={`text-xs ${tone.due}`}>{dueLabel(req)}</Text>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginatedPendientes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-zinc-500">
                    No hay fianzas por emitir
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {/* Freeze divider: lives OUTSIDE the scrolling element, so it is
              pinned to the boundary instead of riding along with the cells. */}
          {frozenPend.scrolled && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute top-0 w-0.5 bg-azul-surelia z-30"
              style={{ left: frozenPend.dividerLeft, bottom: frozenPend.dividerBottom }}
            />
          )}
        </div>
        ) : (
        <div className="relative">
          <Table onScroll={frozen.onScroll}>
            <TableHead className="bg-azul-surelia">
              <TableRow>
                <TableHeader
                  ref={frozen.setRef(0)}
                  style={{ left: frozen.offsets[0] }}
                  className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                >Cliente</TableHeader>
                <TableHeader
                  ref={frozen.setRef(1)}
                  style={{ left: frozen.offsets[1] }}
                  className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                >Número de Fianza</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Responsables</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Prima Total</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Tarifa</TableHeader>
                {isOwner && (
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Comisión</TableHeader>
                )}
                <TableHeader className="text-white font-semibold py-3 border-b-0">Vigencia</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">
                  <div className="flex items-center gap-1">
                    Estatus
                    <span
                      ref={estatusRef}
                      className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-white/60 text-[10px] cursor-help"
                      onMouseEnter={() => {
                        const rect = estatusRef.current.getBoundingClientRect();
                        setEstatusTooltip({ visible: true, x: rect.left + rect.width / 2, y: rect.bottom + 8 });
                      }}
                      onMouseLeave={() => setEstatusTooltip(prev => ({ ...prev, visible: false }))}
                    >?</span>
                  </div>
                </TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Pago</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Tipo de Fianza</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Contrato</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFianzas.map((fianza, idx) => {
                const relacion = relacionChip(fianza);
                return (
                  <TableRow
                    key={`${fianza.contratoId}-${fianza.id || idx}`}
                    className={`group cursor-pointer transition-all duration-200 hover:bg-azul-surelia/5 ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                    }`}
                    onClick={() => handleFianzaClick(fianza)}
                  >
                    <TableCell
                      style={{ left: frozen.offsets[0] }}
                      className={`py-3 ${frozenCellClass(idx)}`}
                    >
                      <button
                        className="text-sm font-medium text-azul-surelia hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (fianza.convenioId) {
                            navigate(`/app/convenios/${fianza.convenioId}`);
                          } else {
                            const params = new URLSearchParams({
                              page: currentPage.toString(),
                              perPage: itemsPerPage.toString(),
                            });
                            navigate(`/app/clientes/${fianza.clientId}?${params.toString()}`, {
                              state: {
                                fromContratosFianzas: true,
                                targetPath: '/app/fianzas',
                                page: currentPage,
                                perPage: itemsPerPage,
                              },
                            });
                          }
                        }}
                      >
                        {fianza.clientName}
                      </button>
                    </TableCell>

                    <TableCell
                      style={{ left: frozen.offsets[1] }}
                      className={`py-3 ${frozenCellClass(idx)}`}
                    >
                      <Text className="text-sm text-zinc-700 font-mono">
                        {fianza.numeroPoliza}
                      </Text>
                      {fianza.endososCount > 0 && (
                        <Text className="text-xs text-zinc-500 mt-0.5">
                          {fianza.endososCount} {fianza.endososCount === 1 ? 'endoso' : 'endosos'}
                        </Text>
                      )}
                      {fianza.endososPendientesCount > 0 && (
                        <span className={`inline-flex items-center gap-1 mt-0.5 text-xs font-medium ${
                          fianza.endososUrgencia === 'vencido' || fianza.endososUrgencia === 'urgente'
                            ? 'text-red-600'
                            : 'text-amber-600'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            fianza.endososUrgencia === 'vencido' || fianza.endososUrgencia === 'urgente'
                              ? 'bg-red-500'
                              : 'bg-amber-500'
                          }`}></span>
                          {fianza.endososPendientesCount === 1
                            ? 'Endoso pendiente'
                            : `${fianza.endososPendientesCount} endosos pendientes`}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="flex items-center gap-2">
                        <AssignedUsersBubbles
                          userIds={[
                            ...(clientsData[fianza.clientId]?.assignedUsers || []),
                            ...(fianza.contractAssignedUsers || [])
                          ].filter((v, i, a) => a.indexOf(v) === i)}
                          maxDisplay={3}
                          size="sm"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFianzaForAssignment(fianza);
                            setIsAssignmentDialogOpen(true);
                          }}
                          className="p-1.5 rounded-lg text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                          title="Editar responsables"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <Text className="text-sm font-semibold text-verde-lima">
                        {fmtMXN(fianza.monto || 0)}
                      </Text>
                    </TableCell>
                    <TableCell className="py-3">
                      <Text className="text-sm font-medium text-zinc-900">
                        {fianza.tarifaCalculada !== null ? `${fianza.tarifaCalculada.toFixed(2)}%` : 'N/A'}
                      </Text>
                    </TableCell>
                    {isOwner && (
                      <TableCell className="py-3">
                        {fianza.comisionMonto != null ? (
                          <>
                            <Text className="text-sm font-semibold text-azul-surelia tabular-nums">
                              {fmtMXN(fianza.comisionMonto)}
                            </Text>
                            {fianza.comisionPct != null && (
                              <div className="flex items-center gap-1">
                                <Text className="text-xs text-zinc-500">{fianza.comisionPct}%</Text>
                                {/* A convenio % is another agent's deal: only the
                                    explicit stamp means a person confirmed it. A
                                    locked comisionPct does not — legacy uploads
                                    stamped the org's own %. */}
                                {fianza.convenioId ? (
                                  fianza.comisionPctConfirmada ? (
                                    <span
                                      className="text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200"
                                      title="El % de comisión de esta fianza de convenio ya fue confirmado."
                                    >
                                      confirmado
                                    </span>
                                  ) : (
                                    <span
                                      className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200"
                                      title="El % de comisión del agente del fiado es estimado; confírmalo al ver el % real."
                                    >
                                      por confirmar
                                    </span>
                                  )
                                ) : fianza.pctEstimado ? (
                                  <span
                                    className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200"
                                    title="Comisión estimada con la tabla de comisiones por defecto; confírmala al ver el % real."
                                  >
                                    por confirmar
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </>
                        ) : (
                          <span
                            className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200"
                            title="Sin tabla de comisiones para esta afianzadora: la comisión quedó pendiente de %. Créala en Configuración > Comisiones y podrás retarificar."
                          >
                            % por confirmar
                          </span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="py-3">
                      <div className="text-xs">
                        <div className="text-zinc-600">
                          <span className="font-medium">Inicio:</span>{' '}
                          {fianza.inicioVigenciaFianza
                            ? new Date(fianza.inicioVigenciaFianza).toLocaleDateString('es-MX')
                            : 'N/A'}
                        </div>
                        <div className="text-zinc-600">
                          <span className="font-medium">Fin:</span>{' '}
                          {fianza.finVigenciaFianza
                            ? new Date(fianza.finVigenciaFianza).toLocaleDateString('es-MX')
                            : 'N/A'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                        STATUS_BADGE[fianza.estatusCalculado] || 'bg-gray-100 text-gray-800'
                      }`}>
                        {fianza.status.label}
                      </span>
                      {relacion && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openRelatedFianza(fianza, relacion.targetId);
                          }}
                          className="mt-1 flex items-center gap-1 text-xs font-medium text-azul-surelia hover:underline"
                        >
                          {relacion.label}
                          <ArrowRightIcon className="w-3 h-3" />
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="py-3">
                      <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${PAGO_BADGE[fianza.pagoEstado]}`}>
                        {pagoLabel(fianza)}
                      </span>
                    </TableCell>
                    <TableCell className="py-3">
                      <Text className="text-sm font-semibold text-zinc-900">
                        {fianza.tipoFianza}
                      </Text>
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="max-w-xs">
                        {fianza.contratoNumero ? (
                          <Text
                            className="text-sm font-mono font-medium text-zinc-800 truncate"
                            title={fianza.contratoNumero}
                          >
                            {fianza.contratoNumero}
                          </Text>
                        ) : (
                          <Text className="text-sm text-zinc-400">Sin número</Text>
                        )}
                        <Text
                          className="text-xs text-zinc-500 truncate"
                          title={fianza.contratoTitle}
                        >
                          {fianza.contratoTitle}
                        </Text>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginatedFianzas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isOwner ? 11 : 10} className="py-8 text-center text-zinc-500">
                    No se encontraron fianzas
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {/* Freeze divider: lives OUTSIDE the scrolling element, so it is
              pinned to the boundary instead of riding along with the cells. */}
          {frozen.scrolled && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute top-0 w-0.5 bg-azul-surelia z-30"
              style={{ left: frozen.dividerLeft, bottom: frozen.dividerBottom }}
            />
          )}
        </div>
        )}

        {/* Pagination Controls (footer of the table card so it stays above the fold) */}
        {rowsTotal > 0 && (
        <div className="border-t border-zinc-200 px-4 py-2">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-zinc-600">
              Mostrando {startIndex + 1} - {Math.min(endIndex, rowsTotal)} de {rowsTotal} {isPorEmitir ? 'requisitos' : 'fianzas'}
            </div>

            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                outline
                color="surelia"
                className="text-sm px-3 py-2"
              >
                Anterior
              </Button>

              <div className="flex items-center gap-1">
                {(() => {
                  const pageNumbers = [];
                  const maxVisiblePages = 5;

                  const totalPagesLocal = Math.max(1, Math.ceil(rowsTotal / itemsPerPage));

                  if (totalPagesLocal <= maxVisiblePages) {
                    for (let i = 1; i <= totalPagesLocal; i++) {
                      pageNumbers.push(i);
                    }
                  } else {
                    pageNumbers.push(1);

                    if (currentPage <= 2) {
                      for (let i = 2; i <= Math.min(3, totalPagesLocal - 1); i++) {
                        pageNumbers.push(i);
                      }
                      if (totalPagesLocal > 3) pageNumbers.push('...');
                    } else if (currentPage >= totalPagesLocal - 1) {
                      if (totalPagesLocal > 3) pageNumbers.push('...');
                      for (let i = Math.max(totalPagesLocal - 2, 2); i <= totalPagesLocal - 1; i++) {
                        pageNumbers.push(i);
                      }
                    } else {
                      pageNumbers.push('...');
                      pageNumbers.push(currentPage);
                      pageNumbers.push('...');
                    }

                    if (totalPagesLocal > 1) pageNumbers.push(totalPagesLocal);
                  }

                  return pageNumbers.map((page, idx) => (
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-zinc-400">...</span>
                    ) : (
                      <Button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        color={currentPage === page ? 'surelia' : 'white'}
                        className="text-sm px-3 py-2 min-w-[40px]"
                      >
                        {page}
                      </Button>
                    )
                  ));
                })()}
              </div>

              <Button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                outline
                color="surelia"
                className="text-sm px-3 py-2"
              >
                Siguiente
              </Button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* FianzaView Modal */}
      {selectedFianza && (
        <FianzaView
          isOpen={isFianzaViewOpen}
          onClose={handleFianzaViewClose}
          fianza={selectedFianza}
          clientData={clientsData[selectedFianza.clientId]}
          contratoData={contratos.find(c => c.id === selectedFianza.contratoId)}
          onUpdate={() => {}}
        />
      )}

      {/* ContratoView Modal (abierto desde un requisito por emitir) */}
      {selectedContrato && (
        <ContratoView
          isOpen={isContratoViewOpen}
          onClose={() => {
            setIsContratoViewOpen(false);
            setSelectedContrato(null);
          }}
          contrato={selectedContrato}
          clientData={clientsData[selectedContrato.clientId]}
          onUpdate={() => {}}
          convenio={selectedContrato.convenioId ? conveniosById[selectedContrato.convenioId] : null}
          beneficiariosById={selectedContrato.convenioId
            ? Object.fromEntries((conveniosById[selectedContrato.convenioId]?.beneficiarios || []).map(b => [b.id, b]))
            : null}
        />
      )}

      {/* Assignment Dialog */}
      <Dialog
        open={isAssignmentDialogOpen}
        onClose={() => setIsAssignmentDialogOpen(false)}
        size="lg"
      >
        <DialogTitle>Responsables del Contrato</DialogTitle>
        <DialogBody>
          {selectedFianzaForAssignment && (
            <UserAssignmentSelector
              assignedUsers={selectedFianzaForAssignment.contractAssignedUsers || []}
              lockedUsers={clientsData[selectedFianzaForAssignment.clientId]?.assignedUsers || []}
              onSave={async (userIds) => {
                const oldUsers = selectedFianzaForAssignment.contractAssignedUsers || [];
                const { description, changes, hasChanges } = computeUserAssignmentChanges(
                  oldUsers,
                  userIds,
                  getUserName
                );

                await updateContractAssignedUsers(selectedFianzaForAssignment.contratoId, userIds);

                if (hasChanges) {
                  try {
                    const clientData = clientsData[selectedFianzaForAssignment.clientId];
                    const contratoTitle = selectedFianzaForAssignment.contratoTitle || 'Sin título';
                    const contratoDescription = `${description} en contrato "${contratoTitle}"`;
                    await logBitacora({
                      action: 'UPDATE',
                      collection: 'contracts',
                      documentId: selectedFianzaForAssignment.contratoId,
                      changes,
                      metadata: {
                        clientId: selectedFianzaForAssignment.clientId,
                        clientName: clientData?.nombreEmpresa || 'Sin nombre',
                        contratoId: selectedFianzaForAssignment.contratoId,
                        description: contratoDescription,
                      },
                      user,
                      userData,
                    });
                  } catch (logError) {
                    console.error('Failed to log contract assignment to bitacora:', logError);
                  }
                }

                setIsAssignmentDialogOpen(false);
                setSelectedFianzaForAssignment(null);
              }}
              onCancel={() => {
                setIsAssignmentDialogOpen(false);
                setSelectedFianzaForAssignment(null);
              }}
            />
          )}
        </DialogBody>
      </Dialog>

      {/* Estatus tooltip (portal) */}
      {estatusTooltip.visible && createPortal(
        <div
          className="fixed w-56 p-2.5 bg-zinc-900 text-white text-xs rounded-lg shadow-lg pointer-events-none"
          style={{ top: estatusTooltip.y, left: estatusTooltip.x, transform: 'translateX(-50%)', zIndex: 9999 }}
        >
          <p className="font-semibold mb-1">Estatus de la fianza:</p>
          <p><span className="font-medium text-green-400">Vigente:</span> Fecha actual entre inicio y fin</p>
          <p><span className="font-medium text-gray-400">Pendiente:</span> Aún no inicia la vigencia</p>
          <p><span className="font-medium text-red-400">Vencida:</span> Ya pasó la fecha de fin</p>
          <p><span className="font-medium text-zinc-400">Cancelada / Anulada / Sustituida:</span> Fianza terminada; el estatus terminal manda sobre las fechas</p>
        </div>,
        document.body
      )}

      {/* Hidden off-screen table for export — mirrors the visible UI table exactly */}
      {EXPORT_ENABLED && isExporting && (
        <div
          ref={exportRef}
          style={{ position: 'fixed', left: 0, top: 0, width: '1400px', backgroundColor: '#ffffff', padding: '24px', zIndex: -1, pointerEvents: 'none' }}
        >
          <div style={{ marginBottom: '16px' }}>
            <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#284865', marginBottom: '4px' }}>Fianzas</h1>
            <p style={{ fontSize: '12px', color: '#71717a' }}>
              Exportado el {new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
              {selectedClient !== 'all' ? ` · Cliente: ${clientNames[selectedClient]}` : ''}
              {selectedEstatus !== 'all' ? ` · Estatus: ${selectedEstatus}` : ''}
              {selectedPago !== 'all' ? ` · Pago: ${selectedPago}` : ''}
            </p>
          </div>
          <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(40,72,101,0.3)', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(to right, #284865, rgba(40,72,101,0.95))' }}>
                  {['Cliente', 'Número de Fianza', 'Prima Total', 'Tarifa', 'Vigencia', 'Estatus', 'Pago', 'Tipo de Fianza', 'Contrato'].map(h => (
                    <th key={h} style={{ color: '#ffffff', fontWeight: 600, padding: '14px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fianzasData.slice(exportFrom - 1, exportTo).map((fianza, idx) => (
                  <tr key={`export-${fianza.contratoId}-${fianza.id || idx}`} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : 'rgba(204,209,170,0.15)' }}>
                    <td style={{ padding: '14px 12px', fontWeight: 500, color: '#284865', fontSize: '13px', verticalAlign: 'middle' }}>{fianza.clientName}</td>
                    <td style={{ padding: '14px 12px', fontFamily: 'monospace', color: '#3f3f46', fontSize: '13px', verticalAlign: 'middle' }}>{fianza.numeroPoliza}</td>
                    <td style={{ padding: '14px 12px', fontWeight: 600, color: '#71717a', fontSize: '13px', verticalAlign: 'middle' }}>
                      {fmtMXN(fianza.monto || 0)}
                    </td>
                    <td style={{ padding: '14px 12px', fontWeight: 500, color: '#18181b', fontSize: '13px', verticalAlign: 'middle' }}>
                      {fianza.tarifaCalculada !== null ? `${fianza.tarifaCalculada.toFixed(2)}%` : 'N/A'}
                    </td>
                    <td style={{ padding: '14px 12px', fontSize: '11px', verticalAlign: 'middle' }}>
                      <div style={{ color: '#52525b' }}>
                        <span style={{ fontWeight: 500 }}>Inicio:</span>{' '}
                        {fianza.inicioVigenciaFianza ? new Date(fianza.inicioVigenciaFianza).toLocaleDateString('es-MX') : 'N/A'}
                      </div>
                      <div style={{ color: '#52525b' }}>
                        <span style={{ fontWeight: 500 }}>Fin:</span>{' '}
                        {fianza.finVigenciaFianza ? new Date(fianza.finVigenciaFianza).toLocaleDateString('es-MX') : 'N/A'}
                      </div>
                    </td>
                    <td style={{ padding: '14px 12px', verticalAlign: 'middle' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        backgroundColor: fianza.estatusCalculado === 'vigente' ? '#dcfce7' : fianza.estatusCalculado === 'vencida' ? '#fee2e2' : fianza.estatusCalculado === 'pendiente' ? '#f3f4f6' : '#e4e4e7',
                        color: fianza.estatusCalculado === 'vigente' ? '#166534' : fianza.estatusCalculado === 'vencida' ? '#991b1b' : fianza.estatusCalculado === 'pendiente' ? '#1f2937' : '#3f3f46',
                      }}>
                        {fianza.status.label}
                      </span>
                    </td>
                    <td style={{ padding: '14px 12px', verticalAlign: 'middle' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        backgroundColor: fianza.pagoEstado === 'pagada' ? '#dcfce7' : fianza.pagoEstado === 'parcial' ? '#fef3c7' : '#ffedd5',
                        color: fianza.pagoEstado === 'pagada' ? '#166534' : fianza.pagoEstado === 'parcial' ? '#92400e' : '#9a3412',
                      }}>
                        {pagoLabel(fianza)}
                      </span>
                    </td>
                    <td style={{ padding: '14px 12px', fontWeight: 600, color: '#18181b', fontSize: '13px', verticalAlign: 'middle' }}>{fianza.tipoFianza}</td>
                    <td style={{ padding: '14px 12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#18181b', fontSize: '13px', verticalAlign: 'middle' }}>{fianza.contratoTitle}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '10px', color: '#a1a1aa', marginTop: '12px', textAlign: 'right' }}>
            Mostrando {Math.min(exportTo, fianzasData.length) - (exportFrom - 1)} de {fianzasData.length} fianzas (posiciones {exportFrom}-{Math.min(exportTo, fianzasData.length)}) · Surelia
          </p>
        </div>
      )}
    </div>
  );
};

export default Fianzas;
